import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import isEqual from 'lodash/isEqual';

import { DataGrid } from '@p81-common/ui';

import InputSearch from 'components/common/forms/InputSearch/InputSearch';
import { useRegionPersistedSearch } from 'contexts/SearchPersistenceContext';
import {
  EmptyState,
  FilterButton,
  Loading,
  PageTitle,
  SaveFilterBlock
} from 'components/common/Elements/Elements';
import FiltersBlock from './parts/FiltersBlock/FiltersBlock';

import { ApplicationsReq, parserData } from './helpers/helpers';
import { getRequest, postRequest } from 'utils/requests';
import {
  replaceHistory,
  checkingForExistenceOfOptions,
  parsConfigForHistory,
  reParsConfigForHistory,
  clearOrDefaultFilters,
  getOriginalTableSettingsListItems,
  onChangeExportButton,
  columnSortHandler,
  onClickCompanyInCell
} from 'utils/common';
import { defaultNumOfRowsOptions } from 'utils/settings.js';
import {
  exportConfig,
  defaultFilters,
  defaultNavigation,
  mainTableConf
} from './helpers/configurates';
import { access, resources, hasAccess } from 'utils/permissions';

import classes from './Applications.module.scss';

const Applications = () => {
  const { t } = useTranslation(['activity']);

  const [filter, setFilter] = useState({
    ...defaultFilters,
    ...defaultNavigation
  });
  const [filterBackend, setFilterBackend] = useState({
    key: 'application',
    value: []
  });
  const [visibleFilterSave, setVisibleFilterSave] = useState(false);
  const [visibleFilterEdit, setVisibleFilterEdit] = useState(false);
  const [changeFilter, setChangeFilter] = useState('Default');
  const [trigerResetDatePicker, setTrigerResetDatePicker] = useState(false);

  //filters and pagination
  const [filterButton, setFilterButton] = useState(false);
  const [tableConf, setTableConf] = useState(
    JSON.parse(JSON.stringify(mainTableConf))
  );

  //data from api
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({});

  //other data
  const triggerSaveTableSettings = false; // we need this const for save Table settings but we not need it for gataGrid
  const [loader, setLoader] = useState(true);
  const [loaderTable, setLoaderTable] = useState(false);
  const [dataConection, setDataConection] = useState(false);
  const [selectLoader, setSelectLoader] = useState(false);

  const [startFilter, setStartFilter] = useState(false);
  const [prevStateFilter, setPrevStateFilter] = useState();
  const [prevStateTableConf, setPrevStateTableConf] = useState();

  const [triggerStartEffect, setTriggerStartEffect] = useState(false);
  const [enabledRequest, setEnabledRequest] = useState(true);

  const [limitRows, setLimitRows] = useState(defaultNumOfRowsOptions);

  const history = useHistory();
  const query = new URLSearchParams(history.location.search);

  const { url } = useRouteMatch();

  const unmount = useRef(false);

  useEffect(() => {
    const htmlTag = document.querySelector('html');
    htmlTag.style.overflowX = 'visible';
    htmlTag.style.width = 'auto';

    return () => {
      htmlTag.style.removeProperty('overflowX');
      htmlTag.style.removeProperty('width');
    };
  }, []);

  const onChangeNumOfRows = limit => {
    return function () {
      setVisibleFilterSave('button');
      if (filter.itemsPerPage === limit) {
        return;
      }
      setFilter({ ...filter, itemsPerPage: limit, currentPage: 1 });
      setLimitRows(
        limitRows.map(rowSizeOption => ({
          ...rowSizeOption,
          selected: rowSizeOption.name === limit
        }))
      );
      setStartFilter(!startFilter);
    };
  };

  function onSettingsSortEnd(args) {
    setVisibleFilterSave('button');
    const { oldIndex, newIndex } = args;
    if (oldIndex !== newIndex) {
      const newArray = columnSortHandler(tableConf, oldIndex, newIndex);
      setTableConf(newArray);
    }
  }

  const handleTableSettingsMenuItemClick = value => {
    return function () {
      setVisibleFilterSave('button');
      const result = tableConf.map(item => {
        const newItem =
          item.headerName === value
            ? {
                ...item,
                show: !item.show,
                selected: !item.show
              }
            : item;

        return newItem;
      });

      setTableConf(result);
    };
  };

  const handleInput = e => {
    setFilter({ ...filter, search: e.target.value, currentPage: 1 });
    setStartFilter(!startFilter);
  };

  // Keep the typed search when switching DR region (P81-132768).
  useRegionPersistedSearch(filter.search, value =>
    handleInput({ target: { value } })
  );

  const handleFilterButton = () => {
    setFilterButton(!filterButton);
  };

  const handleSortable = field => {
    if (field === filter.fieldName) {
      const directionTemp = filter.direction * -1;
      setFilter({ ...filter, direction: directionTemp });
      setStartFilter(!startFilter);
    } else {
      setFilter({ ...filter, fieldName: field });
      setStartFilter(!startFilter);
    }
  };

  const changeCurrentPage = e => {
    setFilter({ ...filter, currentPage: e });
    setStartFilter(!startFilter);
  };

  const onClearSearch = () => {
    setFilter({ ...filter, search: '' });
    setStartFilter(!startFilter);
  };

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  const getFilterOptionsFromBackend = listFilter => {
    setSelectLoader(true);
    getRequest('/rd/applications/list/filters')
      .then(res => {
        if (!unmount.current) {
          const { body } = res;
          setFilters({ ...filters, ...body });
          // we are checking or in the new filter lists there are
          // parameters that are selected, and those that are not, turn off the selection
          const newCheckingList = checkingForExistenceOfOptions(
            listFilter,
            setFilter,
            body,
            true
          );
          newCheckingList && setPrevStateFilter(newCheckingList);
          newCheckingList &&
            replaceHistory(
              filter,
              setEnabledRequest,
              history,
              'validation',
              newCheckingList,
              changeFilter,
              parsConfigForHistory(tableConf)
            );

          setSelectLoader(false);
        }
      })
      .catch(err => {
        !unmount.current && console.log(err);
      });
  };

  useEffect(() => {
    //after the select is closed, we get into this function.
    //We check or after closing the filter whether there have
    //been changes in the selected options and if so, we make a request

    if (triggerStartEffect) {
      if (
        (!isEqual(prevStateFilter, filter) &&
          JSON.parse(query.get('filter')) !== null) ||
        !isEqual(
          parsConfigForHistory(prevStateTableConf),
          parsConfigForHistory(tableConf)
        )
      ) {
        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          false,
          false,
          changeFilter,
          parsConfigForHistory(tableConf)
        );
      }
    }
    setTriggerStartEffect(true);
    // eslint-disable-next-line
  }, [startFilter, tableConf]);

  useEffect(() => {
    if (enabledRequest) {
      setLoaderTable(true);

      if (
        query.get('filter') === null &&
        isEqual(filter, {
          ...defaultFilters,
          ...defaultNavigation
        })
      ) {
        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          'replace',
          false,
          'Default',
          parsConfigForHistory(tableConf)
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
        setPrevStateFilter(filter);
        setPrevStateTableConf(tableConf);
        requestForFilteredData();
      } else if (
        query.get('filter') === null &&
        !isEqual(filter, {
          ...defaultFilters,
          ...defaultNavigation
        })
      ) {
        resetAllfilters(true);
        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          'replace',
          {
            ...defaultFilters,
            ...defaultNavigation
          },
          'Default',
          tableConf
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
      } else {
        setFilter(JSON.parse(query.get('filter')));
        setPrevStateFilter(JSON.parse(query.get('filter')));
        setPrevStateTableConf(
          reParsConfigForHistory(JSON.parse(query.get('tableConf')))
        );
        requestForFilteredData(JSON.parse(query.get('filter')), mainTableConf);
        setChangeFilter(query.get('changeFilter'));
        setTableConf(
          reParsConfigForHistory(JSON.parse(query.get('tableConf')), tableConf)
        );
      }
      setTrigerResetDatePicker(!trigerResetDatePicker);
    }
    setEnabledRequest(true);

    // eslint-disable-next-line
  }, [query.get('filter') || query.get('jarvisRegion')]);

  const requestForFilteredData = (listFilter = filter) => {
    const linkForRequest = `/rd/applications/list?page=${listFilter.currentPage}&limit=${listFilter.itemsPerPage}`;
    const REQ_BODY = ApplicationsReq(listFilter);
    postRequest(linkForRequest, REQ_BODY)
      .then(res => {
        if (!unmount.current) {
          setData(parserData(res.body));
          setDataConection(true);
          setLoader(false);
          setLoaderTable(false);
          getFilterOptionsFromBackend(listFilter);
        }
      })
      .catch(err => {
        if (!unmount.current) {
          setDataConection(false);
          setLoader(false);
          setLoaderTable(false);
          console.log(err);
        }
      });
  };

  const resetAllfilters = (
    reload,
    onlyFilterLabel,
    onClickDefault,
    modeClearAll
  ) => {
    const newValueFilters = clearOrDefaultFilters(modeClearAll, defaultFilters);

    setFilter({ ...newValueFilters, ...defaultNavigation });

    if ((changeFilter === 'Default' || onClickDefault) && !modeClearAll) {
      setVisibleFilterSave(false);
      setVisibleFilterEdit(false);
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    } else {
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
    if (!onlyFilterLabel) {
      setChangeFilter('Default');
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    }

    setTrigerResetDatePicker(!trigerResetDatePicker);
    if (!reload) {
      setStartFilter(!startFilter);
    }
  };

  if (loader) {
    return <Loading />;
  }

  if (!dataConection) {
    return <EmptyState message="connect" />;
  }

  return (
    <>
      <PageTitle title={t('activity:platform.APPLICATIONS')} />
      <div className={classes.filters_main}>
        <InputSearch
          placeholder={t('activity:filters.COMPANY_APP_NAME_PLACEHOLDER')}
          value={filter.search}
          onChange={handleInput}
          onClearSearch={onClearSearch}
          className={classes.filter}
          disabled={loaderTable}
        />

        <FilterButton active={filterButton} onClick={handleFilterButton} />
        <SaveFilterBlock
          setFilterButton={setFilterButton}
          visibleFilterSave={visibleFilterSave}
          setVisibleFilterSave={setVisibleFilterSave}
          visibleFilterEdit={visibleFilterEdit}
          setVisibleFilterEdit={setVisibleFilterEdit}
          filter={filter}
          setFilter={setFilter}
          filterBackend={filterBackend}
          setFilterBackend={setFilterBackend}
          changeFilter={changeFilter}
          setChangeFilter={setChangeFilter}
          resetAllfilters={resetAllfilters}
          defaultNavigation={defaultNavigation}
          trigerResetDatePicker={trigerResetDatePicker}
          setTrigerResetDatePicker={setTrigerResetDatePicker}
          startFilter={startFilter}
          setStartFilter={setStartFilter}
          defaultFilters={defaultFilters}
          tableConf={tableConf}
          defaulTableConf={mainTableConf}
          setTableConf={setTableConf}
          triggerSaveTableSettings={triggerSaveTableSettings}
        />
      </div>
      <FiltersBlock
        filter={filter}
        setFilter={setFilter}
        startFilter={startFilter}
        setStartFilter={setStartFilter}
        filters={filters}
        filterButton={filterButton}
        trigerResetDatePicker={trigerResetDatePicker}
        resetAllfilters={resetAllfilters}
        selectLoader={selectLoader}
        setVisibleFilterSave={setVisibleFilterSave}
        setVisibleFilterEdit={setVisibleFilterEdit}
        changeFilter={changeFilter}
      />
      <div className={classes.tableWrap}>
        <DataGrid
          autoHeight={true}
          classes={{ container: classes.wrapDG }}
          tableConfig={tableConf.filter(item => item.show)}
          tableData={data}
          numOfRowsOptions={limitRows}
          initialState={{
            pinnedColumns: {
              left: [tableConf[0].field]
            }
          }}
          paginationProps={{
            page: filter.currentPage - 1,
            count: data.totalRecords,
            rowsPerPage: limitRows.find(item => item.selected === true).name,
            onChangePage: (ivent, page) => {
              changeCurrentPage(page + 1);
            }
          }}
          toolbarProps={{
            title1: t('activity:platform.APPLICATIONS'),
            title2: data.totalRecords,
            exportButtonProps: {
              onClick: hasAccess([resources.RD_APPLICATIONS, access.EXPORT])
                ? () => {
                    onChangeExportButton(exportConfig, tableConf);
                  }
                : null,
              disabled: !data.totalRecords
            },
            exportTooltipText: 'Export',
            tableSettingsTooltipText: 'Table Settings',
            rowSelectionTooltipText: t('activity:buttons.QUANTITY_OF_ITEMS'),
            onSortEnd: onSettingsSortEnd,
            tableSettingsListItems:
              getOriginalTableSettingsListItems(tableConf),
            onChangeNumOfRows: onChangeNumOfRows,
            handleTableSettingsMenuItemClick,
            tableSettingsChanged: visibleFilterSave
          }}
          onSortModelChange={a => {
            handleSortable(a[0].field);
          }}
          sortingMode={'server'}
          paginationMode={'server'}
          loading={loaderTable}
          rowCount={data.totalRecords}
          onCellClick={cell => onClickCompanyInCell(cell, history, url)}
        />
      </div>
    </>
  );
};

Applications.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string.isRequired
  }).isRequired,
  history: PropTypes.shape({ replace: PropTypes.func.isRequired }).isRequired
};

export default Applications;
